import { NgIf } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnInit, } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { MessageStackService } from '../../services/message-stack.service';
import { TicketService } from '../../services/ticket.service';
import { MessageStackComponent } from "../message-stack/message-stack.component";

/**
 * Detail view for a single ticket: header, participant management, reopen action
 * and the embedded {@link MessageStackComponent} conversation.
 *
 * Reached at `/ticket/<id>`. The id in the URL is the only thing this page needs
 * to exist, so the route can be refreshed, bookmarked or shared.
 *
 * Arriving from the list, the row's subject, status and CC list travel in the
 * navigation state and the conversation has already been fetched on the way.
 *
 * Note that a reload is a halfway case: the browser restores `history.state`,
 * so the header details survive, but every service is rebuilt and the
 * conversation with them. Readiness is therefore judged on whether the thread
 * is actually in hand, never on whether the header details are - keying off the
 * latter renders the message stack against nothing and it comes up empty.
 *
 * For a link opened in a fresh tab there is no state at all and the header is
 * rebuilt from the thread. The one field that cannot be recovered that way is
 * the ticket status: no endpoint returns it for a single ticket, so the chip
 * stays empty until the backend includes it in the ticket/details/ response.
 */
@Component({
  selector: 'app-ticket-details',
  imports: [MessageStackComponent, NgIf, TextBoxModule, FormsModule ],
  templateUrl: './ticket-details.component.html',
  styleUrl: './ticket-details.component.css'
})
export class TicketDetailsComponent implements OnInit {
  /** The ticket being displayed, including its `email_stack` conversation. */
  @Input() ticketDetails!: any;
  /** Existing CC recipients on the ticket; normalised to `[]` in `ngOnInit`. */
  @Input() ccList!: string[] | null | undefined;
  /** Whether the add-participants popup is open. */
  addParticipantsPopup: boolean = false;
  /** Whether the reopen-confirmation popup is open. */
  reopenTicketPopup: boolean = false;
  /**
   * Email address of the signed-in user, used to identify the ticket's creator.
   *
   * Note: the name is misspelled (`tickerCreatorEA`) in the original source.
   */
  tickerCreatorEA!: string;
  /** Raw semicolon-separated text typed into the add-participants field. */
  participants: string = ''
  /** Accumulated valid CC addresses, mirrored to `TicketService.participantsList`. */
  participantsList: string[] = []
  /** Whether the logout confirmation popup is open. */
  openLogoutPopup: boolean = false
  /** Display name of the signed-in user, read from `sessionStorage`. */
  userName!: string;
  /** User-facing result message from the last reopen attempt. */
  reopenTicketResponse: string = ''
  /** Validation message shown under the add-participants field. */
  participantsErrorMessage: string = '';

  /** Opens the reopen-confirmation popup. */
  reopenTicket() {
    this.reopenTicketPopup = true;
  }

  /**
   * Validates the typed addresses and appends the valid, not-yet-listed ones to
   * {@link participantsList}, mirroring the result onto `TicketService` so the next
   * reply carries them.
   *
   * The whole batch is rejected if any address is malformed. The input is cleared
   * either way.
   */
  addParticipants() {
    this.participantsErrorMessage = '';
    const emailRegex = /^[a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]{1,255}\.[a-zA-Z]{2,24}$/;
    let errorMessage = '';

    if (this.participants !== '') {
      const emails = this.participants
        .split(';')
        .map(email => email.trim())
        .filter(email => email);

      const validEmails = emails.filter(email => emailRegex.test(email));
      const invalidEmails = emails.filter(email => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      errorMessage = 'Please provide valid emails separated by semicolons.';
    } else {
      this.participantsList = [
        ...this.participantsList,
        ...validEmails.filter(email => !this.participantsList.includes(email))
      ];
      this.ticketService.participantsList = this.participantsList;
    }
    }
    this.participants = '';

    if (errorMessage) {
      this.participantsErrorMessage = errorMessage;
    } else {
      this.participantsErrorMessage = '';
    }
  }

  /**
   * Removes one CC recipient and re-syncs the list onto `TicketService`.
   *
   * @param participant Email address to drop.
   */
  removeParticipantFromList(participant: string) {
    this.participantsList = this.participantsList.filter(email => email !== participant);
    this.ticketService.participantsList = this.participantsList
  }

  /** Opens the logout confirmation popup. */
  openLogOutComponent() {
    this.openLogoutPopup = true
  }

  /**
   * Reopens the ticket and reflects the new status locally on success.
   *
   * @param ticketID Ticket to reopen.
   */
  changeTicketStatus(ticketID: string) {
    this.ticketService.reopenTicket(ticketID).subscribe({
      next: () => {
        this.reopenTicketResponse = "Ticket is reopened."
        this.ticketDetails.ticket_status = "Reopen"
        this.reopenTicketPopup = false
      },
      error: (error) => {
        console.error(error)
        this.reopenTicketResponse = "Failed to reopen ticket. Please try again..";
      }
    })
  }

  /** Opens the add-participants popup. */
  openAddParticipantsPopup() {
    this.addParticipantsPopup = true;
  }

  /**
   * Asks the parent to switch views.
   *
   * @param path Target view name, e.g. `Help Desk`.
   */
  navigate(path: string) {
    this.router.navigate(['/'])
  }

  /**
   * Seeds the participant list from the incoming CC list and reads the signed-in
   * user from `sessionStorage`.
   *
   * A CC list that is null, undefined, or a single empty string is treated as empty.
   */
  /**
   * Whether the conversation is loaded and the message stack can be built.
   * True immediately when the list handed the thread over, false until the
   * fetch returns on a cold load.
   */
  conversationReady = false;

  ngOnInit(): void {
    const carried = history.state?.ticket;

    if (carried) {
      this.ticketDetails = carried;
      this.ccList = carried.cc_list;
    }

    const routeTicketId = this.route.snapshot.paramMap.get('ticketId');
    const thread = this.messageStackService.messageStack;

    // A cached thread is only this ticket's when the list wrote it for this
    // ticket. The service is a root singleton while history.state is per entry,
    // so back/forward navigation can restore this ticket's header over another
    // ticket's thread - length alone is not evidence of a match.
    const threadIsForThisTicket =
      Array.isArray(thread) &&
      thread.length > 0 &&
      !!routeTicketId &&
      String(this.messageStackService.ticketId) === String(routeTicketId);

    if (threadIsForThisTicket) {
      this.conversationReady = true;
    } else {
      this.loadConversation();
    }

    if (this.ccList === null || this.ccList === undefined || (this.ccList.length === 1 && this.ccList[0] === '')) {
      this.ccList = []
    }
    this.participantsList = this.ccList
    // replyOnTicket sends TicketService.participantsList, not this one, so every
    // assignment here has to be mirrored across or the reply drops its CC list.
    this.ticketService.participantsList = this.participantsList
    this.tickerCreatorEA = sessionStorage.getItem('emailAddress')!
    this.userName = sessionStorage.getItem('userName')!
  }

  /**
   * Fetches the conversation for the id in the URL, for any arrival that did
   * not bring one - a reload or a link opened cold. Where the header details
   * are missing too, the subject and CC list are read back off the thread's
   * first message, which carries both. The status is left blank because
   * nothing returns it for a single ticket.
   * @returns Nothing.
   */
  private loadConversation(): void {
    const ticketId = this.route.snapshot.paramMap.get('ticketId') ?? this.ticketDetails?.ticket_id;

    if (!ticketId) {
      this.router.navigate(['/']);
      return;
    }

    // Anything already known about the ticket wins over these placeholders: on a
    // reload the restored header details are better than what the thread yields.
    this.ticketDetails = {
      ticket_id: ticketId,
      email_subject: '',
      ticket_status: '',
      ...(this.ticketDetails ?? {})
    };

    // getTicketDetails throws outright when there is no session rather than
    // returning a failing observable, so the call needs guarding as well as the
    // subscription - otherwise a link opened before sign-in leaves this page
    // half-built instead of sending the user to the list.
    let request;
    try {
      request = this.ticketService.getTicketDetails(ticketId);
    } catch (error) {
      console.error('[TicketDetailsComponent] Could not load ticket', ticketId, error);
      this.router.navigate(['/']);
      return;
    }

    request.subscribe({
      next: (emailStack: any) => {
        this.messageStackService.messageStack = emailStack;
        const firstEmail = Array.isArray(emailStack) ? emailStack[0] : null;

        this.ticketDetails = {
          ...this.ticketDetails,
          email_subject: this.ticketDetails.email_subject || (firstEmail?.email_subject ?? ''),
          email_stack: emailStack,
          cc_list: this.ticketDetails.cc_list ?? firstEmail?.cc_list ?? []
        };
        this.ccList = this.ticketDetails.cc_list;
        this.participantsList = this.ccList ?? [];
        this.ticketService.participantsList = this.participantsList;
        this.conversationReady = true;
      },
      error: (error) => {
        // An id that is not this user's ticket, or does not exist at all: the
        // list is the only sensible place to land.
        console.error('[TicketDetailsComponent] Could not load ticket', ticketId, error);
        this.router.navigate(['/']);
      }
    });
  }

  /**
   * Closes the participants, logout and reopen popups on any click outside them.
   *
   * @param event The document click being inspected.
   */
  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent): void {
    const addParticipantsDiv = this.elementRef.nativeElement.querySelector('.add-participants-container')
    const addParticipantsBtn = this.elementRef.nativeElement.querySelector('.add-participants-btn')
    const reopenTicketDiv = this.elementRef.nativeElement.querySelector('.reponen-ticket')
    const openLogoutPopup = this.elementRef.nativeElement.querySelector('.log-out-popup')


    if (addParticipantsDiv && !addParticipantsDiv.contains(event.target) && addParticipantsBtn && !addParticipantsBtn.contains(event.target)) {
      this.addParticipantsPopup = false
      this.participantsErrorMessage = ''
    }

    if (openLogoutPopup && !openLogoutPopup.contains(event.target)) {
      this.openLogoutPopup = false
    }

    if (reopenTicketDiv && !reopenTicketDiv.contains(event.target)) {
      this.reopenTicketPopup = false
    }
  }

  /**
   * Clears the session and redirects to the logout page of the portal that matches
   * the current hostname. Unrecognised hostnames clear the session but stay put.
   */
  logout() {
    sessionStorage.clear();
    const currentDomain = window.location.hostname;
    
    if (currentDomain === 'helpdesk.teamleaseregtech.com') {
      window.location.href = 'https://login.teamleaseregtech.com/logout.aspx';
    } else if (currentDomain === 'helpdesk.avantisregtec.in') {
      window.location.href = 'https://uat.avantisregtec.in/logout.aspx';
    } else if (currentDomain === 'impl.teamleaseregtech.com') {
      window.location.href = 'https://impl.teamleaseregtech.com/logout.aspx';
    } else if (currentDomain === 'demohelpdesk.teamleaseregtech.com') {
      window.location.href = 'https://demo.teamleaseregtech.com/logout.aspx';
    } 
  }

  constructor(
    private readonly elementRef: ElementRef,
    private readonly ticketService: TicketService,
    private readonly messageStackService: MessageStackService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}
}
